import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { CardRow } from '../../../../../generic/ui/CardRow';
import { Mono } from '../../../../../generic/ui/Mono';
import { SwipeableListItem } from '../../../../../generic/swipe/SwipeableListAdapter';
import { SwipeAction } from '../../../../../generic/ui/SwipeAction';
import PalletIcon from '../../../../../generic/icons/IconLib/PalletIcon';
import UserIcon from '../../../../../generic/icons/IconLib/UserIcon';
import PlusIcon from '../../../../../generic/icons/IconLib/PlusIcon';
import PrintIcon from '../../../../../generic/icons/IconLib/PrintIcon';
import { convertToDate } from '../../../../../modules/convertDateString';
import { IPackagesItem } from '../../../../../modules/API/Defaults/models/packages';
import { IPalletListWithDetails } from '../../../../../modules/API/Pallets/models/palletList';
import PalletDetailRow from './PalletDetailRow';

export interface PalletRowProps extends IPalletListWithDetails {
    isSelected?: boolean;
    handleSelectPallet: (palletNum: number) => void;
    packageHash?: Record<number, IPackagesItem>;
    isDisabled?: boolean;
    addProduct: (pallet: number) => void;
    activeTab?: string;
    handleSelectProduct?: (item: any) => void;
    deleteProduct: (palletNum: number, recId: number) => void;
    isAllowToEdit: boolean;
    InvoiceSale?: number;
    printPallet?: (palletNum: number) => void;
    /** Controlled expand: pass both to let the parent own it; omitted — local state. */
    expanded?: boolean;
    onToggleExpanded?: (palletNum: number) => void;
}

const PalletRow: React.FC<PalletRowProps> = ({
    Pallet_Num,
    handleSelectPallet,
    isDisabled,
    Pallet_Date,
    ExtermalNum,
    Details,
    packageHash,
    Customer_Name,
    InvoiceSale,
    addProduct,
    activeTab,
    handleSelectProduct,
    deleteProduct,
    isAllowToEdit,
    printPallet,
    expanded: controlledExpanded,
    onToggleExpanded,
}) => {
    const [t] = useTranslation('teudot');
    const [localExpanded, setLocalExpanded] = useState(false);
    const isControlled = controlledExpanded !== undefined;
    const expanded = isControlled ? controlledExpanded : localExpanded;
    const [moving, setMoving] = useState(false);

    const handleToggleSelect = () => {
        if (isDisabled) return;
        setMoving(true);
        setTimeout(() => {
            handleSelectPallet(Pallet_Num);
        }, 500);
    };

    const handleAdd = (e: React.MouseEvent) => {
        e.stopPropagation();
        addProduct(Pallet_Num);
    };
    const handlePrint = (e: React.MouseEvent) => {
        e.stopPropagation();
        printPallet?.(Pallet_Num);
    };
    const handleCardClick = () => {
        if (Details.length === 0) return;
        if (isControlled) onToggleExpanded?.(Pallet_Num);
        else setLocalExpanded((v) => !v);
    };

    const isExistInvoice = Details.some(
        (item) => item.InvoiceSale !== 0 || item.InvoiceBuy !== 0,
    );
    // react-swipeable-list registers touchmove with {capture:true} and
    // calls event.stopPropagation() on horizontal drags — so a parent
    // swipe item always swallows touchmove for any nested SwipeableListItem
    // before the child can see it. To let PalletDetailRow's edit/delete
    // swipe work while the details list is open, block the parent swipe
    // for the duration. When the list is collapsed the parent swipe
    // (take/cancel) works as usual.
    const blockSwipe =
        !isAllowToEdit ||
        (!!InvoiceSale && InvoiceSale !== 0) ||
        isExistInvoice ||
        expanded;
    const formatedDate: string = convertToDate(Pallet_Date);
    const detailsCount = Details.length;
    const hasDetails = detailsCount > 0;

    const isTakenTab = activeTab === t('teudot_create_page_tabs_taken');
    const swipeLabel = isTakenTab
        ? t('teudot_create_page_element_swipe_cancel')
        : t('teudot_create_page_element_swipe_take');

    return (
        <Wrapper $moving={moving}>
            <SwipeableListItem
                swipeLeft={{
                    content: (
                        <SwipeAction
                            variant={isTakenTab ? 'delete' : 'edit'}
                            align='end'
                            label={swipeLabel}
                        />
                    ),
                    action: handleToggleSelect,
                }}
                blockSwipe={blockSwipe}
                threshold={0.35}
                onClick={hasDetails ? handleCardClick : undefined}
            >
                <Card $expanded={expanded} $hasDetailsBelow={hasDetails && expanded}>
                    <CardRow
                        $columns='5.5rem minmax(0, 1fr) auto'
                        $mobileColumns='4.5rem minmax(0, 1fr) auto'
                        $isLast
                        $hover={hasDetails}
                        $interactive={hasDetails}
                    >
                        <CardRow.Cell>
                            <CardRow.Stack $gap='0.125rem'>
                                <CardRow.Line $gap='0.375rem'>
                                    <SmallIcon>
                                        <PalletIcon color='currentColor' size={16} />
                                    </SmallIcon>
                                    <Id>{Pallet_Num}</Id>
                                </CardRow.Line>
                                {!!ExtermalNum && <When>{`(${ExtermalNum})`}</When>}
                                <When>{formatedDate}</When>
                            </CardRow.Stack>
                        </CardRow.Cell>

                        <CardRow.Cell>
                            <CardRow.Stack $gap='0.25rem'>
                                <CardRow.Line $gap='0.5rem'>
                                    <SmallIcon $muted>
                                        <UserIcon color='currentColor' size={16} />
                                    </SmallIcon>
                                    <PrimaryName>{Customer_Name?.trim()}</PrimaryName>
                                </CardRow.Line>
                                {hasDetails && (
                                    <CountLine>
                                        {detailsCount} פריטים
                                    </CountLine>
                                )}
                            </CardRow.Stack>
                        </CardRow.Cell>

                        <CardRow.Cell $align='end'>
                            <Actions onClick={(e) => e.stopPropagation()}>
                                {printPallet && (
                                    <ActionBtn
                                        type='button'
                                        onClick={handlePrint}
                                        aria-label='print pallet'
                                    >
                                        <PrintIcon />
                                    </ActionBtn>
                                )}
                                {isAllowToEdit && (
                                    <ActionBtn
                                        type='button'
                                        onClick={handleAdd}
                                        aria-label='add product'
                                    >
                                        <PlusIcon />
                                    </ActionBtn>
                                )}
                            </Actions>
                        </CardRow.Cell>
                    </CardRow>
                </Card>
            </SwipeableListItem>

            {hasDetails && expanded && (
                <DetailsSection>
                    {Details.map((item, idx) => (
                        <PalletDetailRow
                            key={item.RecID}
                            item={item}
                            packageHash={packageHash}
                            handleSelectProduct={handleSelectProduct}
                            deleteProduct={deleteProduct}
                            isAllowToEdit={isAllowToEdit}
                            isLast={idx === Details.length - 1}
                        />
                    ))}
                </DetailsSection>
            )}
        </Wrapper>
    );
};

export default React.memo(PalletRow);

const Wrapper = styled.div<{ $moving: boolean }>`
    width: 100%;
    transition: transform 0.5s ease-in;
    ${({ $moving }) =>
        $moving &&
        css`
            transform: translate(-110%);
        `}
`;

const Card = styled.div<{ $expanded?: boolean; $hasDetailsBelow?: boolean }>`
    width: 100%;
    border: 1px solid ${({ theme }) => theme.colors.line};
    border-radius: 1rem;
    background: ${({ theme }) => theme.colors.surface};
    overflow: hidden;

    ${({ $hasDetailsBelow }) =>
        $hasDetailsBelow &&
        css`
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
            border-bottom: 0;
        `}
`;

const SmallIcon = styled.span<{ $muted?: boolean }>`
    display: inline-flex;
    align-items: center;
    color: ${({ theme, $muted }) => ($muted ? theme.colors.ink3 : theme.colors.ink2)};
    flex-shrink: 0;
`;

const Id = styled(Mono)`
    font-size: ${({ theme }) => theme.text.lg};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.ink};
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.lg};
    }
`;

const When = styled(Mono)`
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink4};
    white-space: nowrap;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`;

const PrimaryName = styled.span`
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.lg};
    font-weight: 500;
    color: ${({ theme }) => theme.colors.ink};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.lg};
    }
`;

const CountLine = styled.span`
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink3};
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`;

const Actions = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
`;

const ActionBtn = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    padding: 0;
    border-radius: ${({ theme }) => theme.radii.sm};
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.ink};
    border: 1px solid ${({ theme }) => theme.colors.accent};
    cursor: pointer;
    transition: all ${({ theme }) => theme.motion.fast} ease;

    svg {
        width: 0.875rem; height: 0.875rem;
        line-height: 1;
    }

    &:hover {
        background: ${({ theme }) => theme.colors.accentDeep};
        border-color: ${({ theme }) => theme.colors.accentDeep};
    }

    &:active {
        transform: translateY(1px);
    }
`;

const DetailsSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    padding: 0.5rem 0.75rem 0.75rem;
    background: ${({ theme }) => theme.colors.surface};
    border: 1px solid ${({ theme }) => theme.colors.line};
    border-top: 1px solid ${({ theme }) => theme.colors.line2};
    border-bottom-left-radius: 1rem;
    border-bottom-right-radius: 1rem;
`;
